// src/components/Modal/Modal.tsx
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { CloseIcon } from "@/assets/icons";
import styles from "./styles.module.scss";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  hideHeader?: boolean;
  contentClassName?: string;
  wrapperClassName?: string;
  contentClosingClassName?: string;
  wrapperClosingClassName?: string;
  closeAnimationMs?: number;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  hideHeader = false,
  contentClassName,
  wrapperClassName,
  contentClosingClassName,
  wrapperClosingClassName,
  closeAnimationMs = 200,
}) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      return;
    }

    if (!shouldRender) return;

    setIsClosing(true);
    const timeoutId = window.setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
    }, closeAnimationMs);

    return () => window.clearTimeout(timeoutId);
  }, [closeAnimationMs, isOpen, shouldRender]);

  useEffect(() => {
    if (!shouldRender) return;

    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const preventScroll = (e: Event) => e.preventDefault();

    document.addEventListener("keydown", onKey);
    document.addEventListener("wheel", preventScroll, { passive: false });
    document.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("wheel", preventScroll);
      document.removeEventListener("touchmove", preventScroll);
    };
  }, [onClose, shouldRender]);

  return ReactDOM.createPortal(
    shouldRender ? (
      <div className={styles.overlayWrapper}>
        <div
          className={[
            styles.backdrop,
            isClosing ? styles.backdropClosing : "",
          ].join(" ")}
          onClick={onClose}
        />
        <div
          className={[
            styles.modalWrapper,
            isClosing ? styles.modalWrapperClosing : "",
            wrapperClassName,
            isClosing ? wrapperClosingClassName : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={onClose}
        >
          {!hideHeader && (
            <div
              className={styles.modalHeader}
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" onClick={onClose}>
                <CloseIcon />
              </button>
            </div>
          )}
          <div
            className={[
              styles.modalContent,
              contentClassName,
              isClosing ? contentClosingClassName : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        </div>
      </div>
    ) : null,
    document.body
  );
};

export default Modal;
